import { ArrowRight } from 'assets/icons/ArrowRight'
import { Badge } from 'components/Badge/Badge'
import { useTheme } from 'styled-components'
import { Box, Flex } from 'styling/GlobalStyles'
import { ColorVariant, Heading2, MediumText, NormalText } from 'styling/TypographyV2'
import { ProcurementPurchaseRequestListResponse } from 'types/procurement/purchaseRequest.model'
import { PROCUREMENT_REQUEST_CLIENT_STATUS_DISPLAY_META } from 'constants/meta/procurement'
import { Card, CardBody, CardHeader } from 'components/Card/Card'
import { TabProvider, useTabContext } from 'components/Tabs/TabContext'
import { SectionTabs, Tabs } from 'components/Tabs/Tabs'
import { UseQueryResult } from '@tanstack/react-query'
import { ApiPaginatedResponse } from 'constants/globalTypes'
import { JustSpinner } from 'components/Loading/Loading'
import { EmptyList } from 'components/Table/EmptyList'
import { useNavigate } from 'react-router-dom'

export const MyPendingApprovals = ({
  purchaseRequestsQuery,
  actionablePurchaseRequestsQuery,
  myRequestsQuery,
}: {
  purchaseRequestsQuery: UseQueryResult<
    ApiPaginatedResponse<ProcurementPurchaseRequestListResponse>
  >
  actionablePurchaseRequestsQuery: UseQueryResult<
    ApiPaginatedResponse<ProcurementPurchaseRequestListResponse>
  >
  myRequestsQuery: UseQueryResult<ApiPaginatedResponse<ProcurementPurchaseRequestListResponse>>
}) => {
  const { setActiveTab } = useTabContext()
  return (
    <Card>
      <TabProvider tabs={['My Requests', 'Waiting for Approval', 'All Requests']}>
        {(activeTab) => (
          <CardBody padding='0'>
            <SectionTabs />

            <Flex between padding='1rem 1rem 0rem 1rem'>
              <Heading2>{activeTab}</Heading2>
              <Flex gap='8px' pointer='pointer'>
                <NormalText
                  colorVariant={ColorVariant.PRIMARY}
                  hoverUnderline
                  onClick={() => {
                    setActiveTab('Purchase Requests')
                  }}
                  pointer
                >
                  View All
                </NormalText>
                <ArrowRight />
              </Flex>
            </Flex>
            {activeTab === 'My Requests' && <ListPurchaseRequests query={myRequestsQuery} />}
            {activeTab === 'Waiting for Approval' && (
              <ListPurchaseRequests query={actionablePurchaseRequestsQuery} />
            )}
            {activeTab === 'All Requests' && <ListPurchaseRequests query={purchaseRequestsQuery} />}
          </CardBody>
        )}
      </TabProvider>
    </Card>
  )
}

const ListPurchaseRequests = ({
  query,
}: {
  query: UseQueryResult<ApiPaginatedResponse<ProcurementPurchaseRequestListResponse>>
}) => {
  return (
    <Flex column gap='20px' padding='1rem'>
      {query.isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <JustSpinner />
        </div>
      ) : query.data?.data?.length > 0 ? (
        query.data?.data.map((purchaseRequest) => (
          <PendingApprovalCard key={purchaseRequest.id} purchaseRequest={purchaseRequest} />
        ))
      ) : (
        <EmptyList title='No purchase requests found' description='No purchase requests found' />
      )}
    </Flex>
  )
}
const PendingApprovalCard = ({
  purchaseRequest,
}: {
  purchaseRequest: ProcurementPurchaseRequestListResponse
}) => {
  const theme = useTheme() as any
  const userName = `${purchaseRequest.requesterUser?.firstName} ${purchaseRequest.requesterUser?.lastName}`
  const statusMeta = PROCUREMENT_REQUEST_CLIENT_STATUS_DISPLAY_META[purchaseRequest.clientStatus]
  const totalAmount = purchaseRequest.selectedQuote?.items.reduce(
    (acc, item) => acc + item.price,
    0,
  )
  const navigate = useNavigate()
  return (
    <Card onClick={() => navigate(`/procurement/${purchaseRequest.id}`)} hoverBorder noScale>
      <CardBody>
        <Flex column gap='8px'>
          <Flex gap='8px' center>
            <MediumText colorVariant={ColorVariant.SECONDARY}>
              PR - {new Date(purchaseRequest.createdAt).getFullYear()} - {purchaseRequest.id}
            </MediumText>
            {purchaseRequest.clientStatus && (
              <Badge hexColor={statusMeta.color}>{statusMeta.label}</Badge>
            )}
          </Flex>
          <Flex between>
            <Heading2>{purchaseRequest.title}</Heading2>
            {purchaseRequest.currentWorkflowStep && (
              <Badge hexColor={theme.colorOrange}>
                {purchaseRequest.currentWorkflowStep?.name}
              </Badge>
            )}
          </Flex>
          <Flex between>
            <MediumText colorVariant={ColorVariant.SECONDARY}>
              {purchaseRequest.department.name} • {userName}{' '}
            </MediumText>
            {totalAmount && (
              <MediumText uppercase>
                $ {purchaseRequest.selectedQuote?.items.reduce((acc, item) => acc + item.price, 0)}
              </MediumText>
            )}
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  )
}
